import React, { useCallback, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Box } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';
import MobileRowMainContent from './components/MobileRowStructure';
import BuildColumns from './helpers/Columns';
import CreateEditProductCard from './components/CreateEditProductCard';
import useBusinessInformation from '../../../helperHooks/useBusinessInformation';
import { StepContext } from '../../../context/StepContext';
import useFiltersFetchedData from '../../../helperHooks/useFiltersFetchedData';
import { CATEGORIES_API_CACHE_KEY, PRODUCTS_API_CACHE_KEY } from '../../../constants';
import PageHeader from '../../../components/pageHeaderComponents/PageHeader';
import GenericDataGridAndSideForm from '../../../components/datagridComponents/GenericDataGridAndSideForm';
import ActionsContainer from '../../../components/ActionsContainer';
import { Category, CategoryRaw, Product, ProductErrors, ProductRaw } from '../../../types';
import { API_V1_HOSTNAME_KEY } from '../../../localStorageKeys';
import { UserContext } from '../../../context/UserContext';
import usePageTitle from '../../../helperHooks/usePageTitle';

const Products = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    currencyISOCode,
    currencySymbol,
    isLoading: isLoadingCurrency,
  } = useBusinessInformation();
  const { goToNextStep } = useContext(StepContext);
  const { locale } = useContext(UserContext);

  usePageTitle('nav.products');

  const {
    fetchData: refetch,
    data: productsRawData,
    isLoading,
    isRefetching,
    isError,
    error,
  } = useFiltersFetchedData<{
    Data: ProductRaw[];
    Metadata: {
      Page: number;
      Limit: number;
      TotalPages: number;
      TotalRecords: number;
      CurrentPageSize: number;
    };
  }>(
    PRODUCTS_API_CACHE_KEY,
    `${localStorage.getItem(API_V1_HOSTNAME_KEY)}onboarding/proxy/products`,
    'products'
  );

  const gridData: Product[] = useMemo(
    () =>
      productsRawData && productsRawData?.Data?.length > 0
        ? productsRawData?.Data?.map((x) => ({
            ...x,
            id: x?.Id,
          }))
        : [],
    [productsRawData]
  );

  const { data: categoryRawData, isLoading: isLoadingCategoryData } = useFiltersFetchedData<{
    Data: CategoryRaw[];
    Metadata: {
      Page: number;
      Limit: number;
      TotalPages: number;
      TotalRecords: number;
      CurrentPageSize: number;
    };
  }>(
    CATEGORIES_API_CACHE_KEY,
    `${localStorage.getItem(API_V1_HOSTNAME_KEY)}onboarding/proxy/categories`,
    'categories'
  );

  const categoriesData: Category[] = useMemo(
    () =>
      categoryRawData && categoryRawData?.Data?.length > 0
        ? categoryRawData?.Data?.map((x) => ({
            ...x,
            id: x?.Id,
          }))
        : [],
    [categoryRawData]
  );

  const buildColumns = useCallback(
    (): GridColDef[] =>
      BuildColumns({
        categoriesData,
        currencyInfo: { currencyISOCode, currencySymbol, isLoading: isLoadingCurrency },
        locale,
      }),
    [categoriesData, currencyISOCode, currencySymbol, isLoadingCurrency, locale]
  );

  const unauthorized = (error as any)?.code === 401;

  return (
    <>
      <PageHeader omitTextHeader />
      <GenericDataGridAndSideForm
        pageDescription={t('screens.products.products.description')}
        data={gridData}
        metadata={{
          page: productsRawData?.Metadata?.Page,
          limit: productsRawData?.Metadata?.Limit,
          totalPages: productsRawData?.Metadata?.TotalPages,
          totalRecords: productsRawData?.Metadata?.TotalRecords,
          currentPageSize: productsRawData?.Metadata?.CurrentPageSize,
        }}
        isLoading={isLoading || isRefetching}
        isError={isError}
        unauthorized={unauthorized}
        fetchData={refetch}
        BuildColumns={buildColumns}
        CreateComponent={CreateEditProductCard}
        translationKey="products.products"
        endpoint={`${localStorage.getItem(API_V1_HOSTNAME_KEY)}onboarding/proxy/products`}
        sentryMsg="products"
        cacheKey={PRODUCTS_API_CACHE_KEY}
        mobileRowMainContent={MobileRowMainContent}
      />
      <ActionsContainer>
        <Button
          variant="outlined"
          sx={{
            margin: '0px',
            flexGrow: '0',
            height: '48px',
          }}
          onClick={() => {
            navigate('/products-categories');
          }}
        >
          {t('actions.previous')}
        </Button>
        <Button
          variant="contained"
          sx={{
            margin: '0px',
            flexGrow: '0',
            height: '48px',
          }}
          onClick={() => {
            goToNextStep();
          }}
        >
          {t('actions.next')}
        </Button>
      </ActionsContainer>
    </>
  );
};

export default Products;
